import type { UploadMode } from '../../../shared/protocol.ts';
import { formatBytes, formatRate } from '../lib/emitter.ts';
import { sharedUploads } from '../lib/studio.ts';
import type { LocalCopyPolicy } from '../lib/uploader.ts';
import { Icon, useObservable } from './common.tsx';
import { LocalCopies } from './LocalCopies.tsx';

const MODES: { mode: UploadMode; label: string; hint: string }[] = [
  { mode: 'smart', label: 'Smart', hint: 'Upload during the call, backing off whenever the call needs bandwidth' },
  { mode: 'after', label: 'After take', hint: 'Hold uploads until recording stops' },
  { mode: 'max', label: 'Full speed', hint: 'Use all available bandwidth (call quality may drop)' },
];

/** Upload queue for this browser. Shown in the studio sidebar and on the home page (for resumes). */
export function UploadsPanel({ studioId, compact }: { studioId?: string; compact?: boolean }) {
  const { uploader, governor } = sharedUploads();
  useObservable(uploader);
  useObservable(governor);
  const all = uploader.jobs.filter((j) => !studioId || j.studioId === studioId);
  // Files of keep-local takes aren't uploads: they're listed (and can be sent) on the "On this computer" page.
  const kept = all.filter((j) => j.keepLocal && !j.done);
  const jobs = all.filter((j) => !kept.includes(j));
  const pending = jobs.filter((j) => !j.done);
  const { total, uploaded } = uploader.totals(jobs);
  if (!all.length && !studioId) return null;
  const allowed = governor.allowedBps();

  return (
    <div className={compact ? 'col' : 'card col'} style={{ gap: 14 }}>
      <div className="col" style={{ gap: 8 }}>
        <div className="section-title">
          <h3>{pending.length ? `Uploading ${pending.length} file${pending.length > 1 ? 's' : ''}` : 'Uploads from this computer'}</h3>
          <span className="pill tnum">
            {formatBytes(uploaded)} / {formatBytes(total)}
          </span>
        </div>
        <div className={`bar ${!pending.length && jobs.length ? 'ok' : ''}`}>
          <i style={{ width: `${total ? (uploaded / total) * 100 : 0}%` }} />
        </div>
        <div className="small muted">
          {!uploader.online
            ? 'Offline. Uploads resume automatically when you reconnect.'
            : governor.pressureHold && governor.recording
              ? 'Paused while this computer is busy, so the recording gets its full attention. Uploads resume on their own.'
              : allowed === 0
              ? 'Holding uploads until the take ends.'
              : uploader.currentBps
                ? `Sending at ${formatRate(uploader.currentBps)}${Number.isFinite(allowed) ? ` · limited to ${formatRate(allowed)} to protect the call` : ''}`
                : pending.length
                  ? 'Waiting for more recorded data…'
                  : jobs.length
                    ? 'Everything is safely on the server.'
                    : 'Recordings upload here automatically.'}
          {governor.congested && allowed > 0 ? ' · the call is congested, backing off' : ''}
        </div>
        {kept.length > 0 && (
          <div className="alert info small">
            <Icon name="folder" size={13} />
            <span>
              {kept.length} file{kept.length > 1 ? 's' : ''} from keep-local takes stay on this computer.{' '}
              <a href="/local" target={studioId ? '_blank' : undefined} rel="noreferrer">
                On this computer
              </a>
            </span>
          </div>
        )}
        {uploader.lastError && (
          <div className="alert warn small">
            <Icon name="warn" size={13} /> {uploader.lastError}
          </div>
        )}
      </div>

      <div className="field">
        Upload speed
        <div className="toggle full" role="radiogroup" aria-label="Upload mode">
          {MODES.map((m) => (
            <button key={m.mode} role="radio" aria-checked={governor.mode === m.mode} className={governor.mode === m.mode ? 'active' : ''} title={m.hint} onClick={() => governor.setMode(m.mode)}>
              {m.label}
            </button>
          ))}
        </div>
        <span className="field-hint">{MODES.find((m) => m.mode === governor.mode)?.hint}</span>
      </div>

      {jobs.length > 0 && (
        <div className="col" style={{ gap: 10 }}>
          <span className="eyebrow">Files</span>
          {jobs.slice(-12).reverse().map((j) => {
            const size = j.size ?? Math.max(j.uploaded, 1);
            const empty = j.done && j.size === 0;
            return (
              <div key={j.trackId} className="col" style={{ gap: 4 }}>
                <div className="row between small">
                  <span className="ellipsis">
                    <span className="faint">Take {j.takeNumber} ·</span> {j.label}
                  </span>
                  <span className={empty ? 'faint' : j.done ? '' : 'muted tnum'} style={j.done && !empty ? { color: 'var(--ok)', display: 'inline-flex', alignItems: 'center', gap: 4 } : undefined}>
                    {empty ? (
                      'nothing recorded'
                    ) : j.done ? (
                      <>
                        <Icon name="check" size={13} /> uploaded
                      </>
                    ) : j.size == null ? (
                      `${formatBytes(j.uploaded)} · recording`
                    ) : (
                      `${Math.floor((j.uploaded / size) * 100)}%`
                    )}
                  </span>
                </div>
                {!j.done && (
                  <div className="bar">
                    <i style={{ width: `${j.size ? (j.uploaded / size) * 100 : 100}%`, opacity: j.size ? 1 : 0.35 }} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      <label className="field">
        Local copies after upload
        <select value={uploader.localCopyPolicy} onChange={(e) => uploader.setLocalCopyPolicy(e.target.value as LocalCopyPolicy)}>
          <option value="keep">Keep until I clear them</option>
          <option value="delete-when-uploaded">Delete once the server confirms the full file</option>
          <option value="delete-after-7-days">Delete 7 days after upload</option>
        </select>
      </label>
      {uploader.keptLocalBytes > 0 && (
        <button className="sm" onClick={() => void uploader.clearUploaded()} title="Only files the server has confirmed in full are removed" style={{ alignSelf: 'flex-start' }}>
          Clear {formatBytes(uploader.keptLocalBytes)} of uploaded local copies
        </button>
      )}
      {compact && <LocalCopies studioId={studioId} compact />}
    </div>
  );
}
